import { useEffect, useRef, useState } from "react";
import { fechaLlana } from "../../../shared/piezas/index.ts";
import type { Hallazgos } from "../../../shared/piezas/index.ts";
import { FICHA, MARCA_POSIBLE, SAT, texto } from "../../../shared/rotulos/index.ts";
import { vistaAnteElSat } from "../../../argus-team-web/web/src/lib/ante-el-sat.ts";
import type { TarjetaVista } from "../../../argus-team-web/web/src/lib/ante-el-sat.ts";
import { type CertificadoSat, type ClienteNexo, type SujetoFicha, vizCertificados } from "../api.ts";
import { accionDeError, esCancelacion, mensajeDeError, razonDe } from "./mensajes.ts";

/**
 * La caja «Ante el SAT» de la ficha de Nexo (corte 2 de los certificados del SAT; decisión 4: Nexo la ve), la misma que la de Argus Team:
 * la arma lib/ante-el-sat.ts de Argus Team (sin React ni red; se importa, no se copia, como lib/ficha.ts) y aquí sólo se pinta. Todo
 * sale de `ante_el_sat`; es contexto: no cambia el nivel. Lo posible lleva su marca y su causa.
 */
export function AnteElSat({ cliente, sujeto, doc, onBuscarDeNuevo }: { cliente: ClienteNexo; sujeto: SujetoFicha; doc: Hallazgos; onBuscarDeNuevo?: (() => void) | undefined }) {
  const v = vistaAnteElSat(doc);
  if (!v) return null;
  const empresa = doc.sujeto.tipo === "empresa";
  return (
    <section className="nx-caja nx-sat" data-parte="ante-el-sat">
      <h3 className="nx-rotulo">{v.titulo}</h3>
      {v.empresaPosible ? <p><Posible /> {v.empresaPosible}</p> : null}
      {v.tarjetas.map((t) => <Tarjeta key={t.clave} t={t} empresa={empresa} />)}
      {v.correos.length > 0 ? <ul className="nx-sat-lista">{v.correos.map((c, i) => <li key={i}>{c}</li>)}</ul> : null}
      {v.hayPosible ? <p className="nx-tenue">{SAT.posibleAyuda}</p> : null}
      <Certificados cliente={cliente} sujeto={sujeto} omitido={v.omitido} onBuscarDeNuevo={onBuscarDeNuevo} />
      <p className="nx-tenue">{v.ayuda}</p>
    </section>
  );
}

/** La marca «posible» (la del buscador, aprobada el 2026-10-04). */
function Posible() {
  return <span className="nx-posible">{MARCA_POSIBLE}</span>;
}

function Tarjeta({ t, empresa }: { t: TarjetaVista; empresa: boolean }) {
  return (
    <div className="nx-sat-tarjeta" data-nivel-liga={t.posible ? "posible" : "confirmada"}>
      <h4>
        {t.nombre ?? t.sinNombre}
        {t.nombreDe ? <span className="nx-tenue"> {t.nombreDe}</span> : null}
        {t.enLista.map((e) => (
          <span key={e.ref} className="nx-sat-en-lista"> · {SAT.enLaLista}{e.posible ? <> <Posible />{e.causa ? <span className="nx-tenue">: {e.causa}</span> : null}</> : null}</span>
        ))}
      </h4>
      {t.posible ? <p><Posible />{t.causa ? <span className="nx-tenue">: {t.causa}</span> : null}</p> : null}
      {t.otrasListas.map((o, i) => <p key={i} className="nx-tenue">{o}</p>)}
      {t.datos.length > 0 || t.poblacion ? (
        <>
          <h5 className="nx-sat-parte">{SAT.quienEs}</h5>
          {t.datos.length > 0 ? (
            <dl className="nx-sat-datos">
              {t.datos.map((d, i) => <div key={`${d.rotulo}-${String(i)}`}><dt>{d.rotulo}</dt><dd>{d.valor}</dd></div>)}
            </dl>
          ) : null}
          {t.poblacion ? <p className="nx-tenue">{t.poblacion}</p> : null}
        </>
      ) : null}
      {t.representa.length > 0 ? (
        <>
          {/* En la ficha de una empresa, la línea es la de esa empresa (S13): sin el subtítulo de la persona. */}
          {!empresa ? <h5 className="nx-sat-parte">{SAT.queRepresenta}</h5> : null}
          <ul className="nx-sat-lista">{t.representa.map((r, i) => <li key={i}>{r.posible ? <><Posible />{" "}</> : null}{r.texto}</li>)}</ul>
        </>
      ) : null}
      {t.conQuien.length > 0 ? (
        <>
          <h5 className="nx-sat-parte">{SAT.conQuien}</h5>
          <ul className="nx-sat-lista">{t.conQuien.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * «Ver todos los certificados» (S19), al pedirlo. El mismo botón abre y cierra (aria-expanded). Con la liga sólo posible no se muestra el
 * correo (D1) y la marca «posible» va donde está la duda: en el representante o en la empresa, según el papel del registro.
 */
function Certificados({ cliente, sujeto, omitido, onBuscarDeNuevo }: {
  cliente: ClienteNexo; sujeto: SujetoFicha; omitido: string | null; onBuscarDeNuevo: (() => void) | undefined;
}) {
  const [estado, setEstado] = useState<"cerrado" | "cargando" | { total: number; lista: CertificadoSat[] } | { error: string; razon: string }>("cerrado");
  const control = useRef<AbortController | null>(null);
  useEffect(() => () => { control.current?.abort(); }, []);
  const pedir = () => {
    control.current?.abort();
    const c = new AbortController();
    control.current = c;
    setEstado("cargando");
    vizCertificados(cliente, sujeto, c.signal).then(
      (r) => { if (!c.signal.aborted) setEstado({ total: r.total, lista: r.certificados }); },
      (e: unknown) => {
        if (c.signal.aborted || esCancelacion(e)) return;
        const razon = razonDe(e);
        setEstado({ error: mensajeDeError(razon), razon });
      },
    );
  };
  const abierto = estado !== "cerrado";
  const alternar = () => { if (abierto) { control.current?.abort(); setEstado("cerrado"); } else pedir(); };
  const fecha = (f: string | null) => (f ? fechaLlana(f) : "");
  return (
    <div className="nx-sat-certificados">
      <p><button type="button" className="nx-liga" aria-expanded={abierto} onClick={alternar}>{abierto ? SAT.ocultarTodos : omitido ?? SAT.verTodos}</button></p>
      {estado === "cargando" ? <div className="nx-esqueleto" aria-busy="true" /> : null}
      {typeof estado === "object" && "error" in estado ? (
        <p className="nx-error" role="alert">{estado.error}{" "}
          {accionDeError(estado.razon) === "buscar"
            ? (onBuscarDeNuevo ? <button type="button" className="nx-btn" onClick={onBuscarDeNuevo}>{FICHA.buscarDeNuevo}</button> : null)
            : accionDeError(estado.razon) === "reintentar" ? <button type="button" className="nx-btn" onClick={pedir}>{FICHA.reintentar}</button> : null}
        </p>
      ) : null}
      {typeof estado === "object" && "lista" in estado ? (
        <>
          <h4>{SAT.tituloDetalle}</h4>
          {estado.lista.length === 0 ? <p className="nx-tenue">{SAT.sinCertificados}</p> : (
            <div className="nx-sat-tabla">
              <table>
                <thead><tr>
                  <th scope="col">{SAT.colEmpresa}</th><th scope="col">{SAT.colRepresentante}</th><th scope="col">{SAT.colEmitido}</th>
                  <th scope="col">{SAT.colVence}</th><th scope="col">{SAT.colSerie}</th><th scope="col">{SAT.colSucursal}</th><th scope="col">{SAT.colCorreo}</th>
                </tr></thead>
                <tbody>{estado.lista.map((c, i) => {
                  const posible = c.nivel === "posible";
                  return (
                    <tr key={i} data-nivel-liga={c.nivel}>
                      <td>{c.empresa}{posible && c.papel === "titular" ? <> <Posible /></> : null}</td>
                      <td>{[c.rep_curp, c.rep_rfc].filter((x): x is string => !!x).map((x) => <span key={x} className="nx-sat-clave">{x}</span>)}{posible && c.papel === "representante" ? <Posible /> : null}</td>
                      <td className="fecha">{fecha(c.emitido)}</td><td className="fecha">{fecha(c.vence)}</td>
                      <td>{c.numero_serie}</td><td>{c.sucursal}</td>
                      <td>{posible ? "" : c.correo}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
          {estado.total > estado.lista.length ? <p className="nx-tenue">{texto(SAT.mostrados, { n: estado.lista.length, total: estado.total })}</p> : null}
        </>
      ) : null}
    </div>
  );
}
